import { FC, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button, ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import useConversation from "@/hooks/useConversation";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { Permission } from "@/lib/permissions/Permission.enum";
import { logger } from "@/lib/logger";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";

/**
 * Admin delete of any user's conversation, with its confirm dialog — the one
 * copy behind both tenant-admin surfaces: the Insights list row (LAR-367 #4,
 * where admins triage) and the per-conversation drill-down.
 *
 * In the list, deleting keeps the admin in place — the mutation invalidates the
 * conversation queries and the row returns marked "Slettet"; the drill-down
 * passes `onDeleted` to navigate away instead. Rendering nothing without
 * DeleteAnyConversation makes the component safe wherever it is dropped in.
 *
 * `stopPropagation` on the trigger is load-bearing: table rows navigate on
 * click. The confirm itself is a shared-ui `ConfirmationDialog`, whose
 * overlay and content already stop clicks from leaking through the portal
 * (LAR-214), so it needs no handler of its own.
 */
type ConversationDeleteActionProps = {
  conversationId: string;
  conversationTitle?: string;
  /** `icon` for a table row, `labelled` for a page header. */
  appearance?: "icon" | "labelled";
  /** Called after a successful delete (the drill-down navigates away). */
  onDeleted?: () => void;
};

const ConversationDeleteAction: FC<ConversationDeleteActionProps> = ({
  conversationId,
  conversationTitle,
  appearance = "icon",
  onDeleted,
}) => {
  const [open, setOpen] = useState(false);
  const { deleteConversationAsAdmin } = useConversation();
  const hasPermission = useHasPermission();

  if (!hasPermission({ allOf: [Permission.DeleteAnyConversation] }))
    return null;

  const handleDelete = async () => {
    try {
      await deleteConversationAsAdmin.mutateAsync(conversationId);
      setOpen(false);
      showSuccessToast("Samtalen er slettet.");
      onDeleted?.();
    } catch (error) {
      logger.error("Failed to delete conversation as admin", {
        operation: "deleteConversationAsAdmin",
        error,
        conversationId,
      });
      showErrorToast("Kunne ikke slette samtalen.");
    }
  };

  return (
    <>
      <Button
        variant={appearance === "labelled" ? "outline" : "ghost"}
        size="sm"
        className={
          appearance === "labelled"
            ? "flex text-xs text-destructive"
            : undefined
        }
        aria-label={`Slet samtale: ${conversationTitle || "Uden titel"}`}
        disabled={deleteConversationAsAdmin.isPending}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <Trash2
          className={
            appearance === "labelled"
              ? "mr-1 size-4"
              : "size-4 text-destructive"
          }
        />
        {appearance === "labelled" ? "Slet samtale" : null}
      </Button>
      <ConfirmationDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={handleDelete}
        confirmPending={deleteConversationAsAdmin.isPending}
        title="Slet samtale?"
        description="Hele samtalen og alle dens beskeder slettes. Handlingen kan ikke fortrydes."
        confirmText="Slet samtale"
        variant="destructive"
      />
    </>
  );
};

export default ConversationDeleteAction;
